import { defineStore } from "pinia";

import { useUserStore } from "./user";
import { useNotificationStore } from './notification';

import { setKey as setBrowserStorageKey } from "./helpers/localStorage.mjs";
import { fetchFromBackend } from "./helpers/backend.mjs";

export const usePerformanceStore = defineStore("performance", {
  state: () => {
    return {
      performances: [],
    }
  },
  getters: {
    // return the full performance data, the signature allows for finding the performance by display (i.e. campaign or halfTime) and campaign.id
    performance: (state) => {
      if (!state.performances) return false;
      const userStore = useUserStore();
      let signature = null;
      if (userStore.isManagement) {
        signature = `management:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`;
      } else if (userStore.currentSelection?.salesAgent?.id !== -1) {
        signature = `sA:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`;
      } else if (userStore.currentSelection.salesTeam.id !== -1) {
        signature = `sT:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`;
      }
      const performance = state.performances.find((performance) => performance.signature === signature);
      if (!performance) return false;
      return performance.data;
    },
  },
  actions: {
    /** see if the incentives need to be fetched again, either because the data is not up to date or the display status has changed
    */
    async refresh() {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();

      // console.log('refreshing performance with scope', userStore.currentSelection.scope)
      // if (userStore.currentSelection?.scope !== 'salesAgent') return false;

      // create a signature to see if the data is is up to date or needs to be fetched
      const signature = (userStore.isManagement)
        ? `management:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`
        : (userStore.currentSelection?.salesAgent?.id !== -1)
          ? `sA:${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`
          : `sT:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`;      
      const performanceExists = this.performances.find((performance) => performance.signature === signature);
      // return the existing data if it exists and its update property is less then 6 hours old
      if (performanceExists && performanceExists?.update > (Date.now() - (6 * 60 * 60 * 1000))) return performanceExists;
      // fetch the data from the backend, get both halftime and campaign data
      const taskList = [
        fetchFromBackend("performanceRadar/v1/salesAgent", {
          id: userStore.id,
          filter: {
            mode: (userStore.currentSelection.campaign.id <= 22) ? 'ht' : 'month',
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgents: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeams: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          }
        }),
        fetchFromBackend("performanceRadar/v1/salesAgent", {
          id: userStore.id,
          filter: {
            mode: '',
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgents: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeams: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          }
        }),
      ];
      userStore.currentSelection.performance.isLoading = true;
      let responses = null;
      try {
        responses = await Promise.all(taskList)
        // notificationStore.addNotification('Performance-Daten erfolgreich geladen!', 'success');
      } catch (error) {
        userStore.currentSelection.performance.isLoading = false;
        notificationStore.addNotification(`Performance-Daten konnten nicht geladen werden! (${error?.message || error})`, 'error', 19);
        return false;
      }
      userStore.currentSelection.performance.isLoading = false;
      if (!responses || !responses[0]?.salesAgentStatus || !responses[0]?.statusList || !responses[1]?.salesAgentStatus || !responses[1]?.statusList) {
        notificationStore.addNotification('Performance-Daten unvollständig oder ungültig!', 'error', 20);
        return false;
      }

      userStore.status = responses[0].salesAgentStatus
      // debug:
      // userStore.status.planned = 'Advanced';
      const status = responses[0].statusList.find((s) => s.name === userStore.status.planned);
      userStore.status.achieved = (status) ? status.achieved : false;
      const performanceData = {}
      for (const [index, segment] of ['halftime', 'campaign'].entries()) {
        const plannedStatus = responses[index].statusList.find((s) => s.name === responses[index].salesAgentStatus.planned);
        console.log(
          'planned Status', plannedStatus, '\n',
          'performance setting status to', responses[0].salesAgentStatus, '\n',
          'statusList', responses[0].statusList, '\n',
          'searching', userStore.status.planned
        )
        if (!plannedStatus) continue;
        performanceData[segment] = {
          status: {
            currentStatus: responses[0].salesAgentStatus.current,
            plannedStatus: responses[0].salesAgentStatus.planned,
            commission: responses[index].salesAgentStatus.commission,
            commissionPoints: responses[index].salesAgentStatus.commissionpoints,
            commissionOnTop: responses[index].salesAgentStatus.commissionOnTop,
            achieved: responses[index].salesAgentStatus.achieved,
            statusList: responses[index].statusList,
          },
          salesEuro: {
            planned: plannedStatus.plannedValues.salesEuro,
            current: responses[index].actualValues.salesEuro,
            expired: Math.round(responses[index].actualValues.salesEuro / plannedStatus.plannedValues.salesEuro * 100)
          },
          salesEuroNewCustomers: {
            planned: plannedStatus.plannedValues.salesEuroNewCustomers,
            current: responses[index].actualValues.salesEuroNewCustomers,
            expired: Math.round(responses[index].actualValues.salesEuroNewCustomers / plannedStatus.plannedValues.salesEuroNewCustomers * 100)
          },
          salesNumberNewCustomers: {
            planned: plannedStatus.plannedValues.salesNumberNewCustomers,
            current: responses[index].actualValues.salesNumberNewCustomers,
            expired: Math.round(responses[index].actualValues.salesNumberNewCustomers / plannedStatus.plannedValues.salesNumberNewCustomers * 100)
          },
          progress: {
            planned: responses[index].progress.plannedTotal,
            current: responses[index].progress.plannedCurrent,
            expired: Math.round(responses[index].progress.plannedCurrent / responses[index].progress.plannedTotal * 100)
          },
          units: responses[index].units
        }
      }
      // if the data exists update it, if not create it
      if (performanceExists) {
        Object.assign(performanceExists, { update: Date.now(), data: performanceData });
      } else {
        // console.log('pushing performance', performanceData)
        this.performances.push({ signature: signature, update: Date.now(), data: performanceData })
      }
      console.log('performance', performanceData)
    },
  },
});
